import { useCallback, useEffect, useRef, useState } from "react";
import { useBlocker } from "react-router";
import type { CanvasDocumentV3 as CanvasDocument, CanvasViewV3 as CanvasView } from "@seedance/contracts";
import { apiClient, type ApiClient } from "../api/client.js";
import {
  createCanvasDraftStore,
  type CanvasDraft,
  type CanvasDraftListing,
  type CanvasDraftStore
} from "./draft-store.js";
import {
  createCanvasSessionController,
  type CanvasSessionController,
  type CanvasRecoveryState,
  type SaveState
} from "./session-controller.js";
import { confirmCanvasNavigation, exportLatestCanvas, protectCanvasUnload } from "./view-state.js";
import { canvasCopy } from "../copy/zh-CN.js";

export interface UseCanvasSessionOptions {
  ownerId: string;
  projectId: string;
  projectName: string;
  initialCanvas: CanvasView;
  client?: ApiClient;
  drafts?: CanvasDraftStore;
  navigate?(path: string): void;
}

export interface CanvasSession {
  document: CanvasDocument;
  saveState: SaveState;
  recoveryState: CanvasRecoveryState;
  recoveryDrafts: CanvasDraft[];
  recoveryError: string | undefined;
  refreshRecoveryDrafts(): Promise<void>;
  restoreDraft(draft: CanvasDraft): void;
  exportLocal(): void;
  change(document: CanvasDocument): void;
  retry(): void;
  getMutationVersion(): number;
  loadServer(): Promise<void>;
  applyServerCommand(
    view: CanvasView,
    merge: (current: CanvasDocument, server: CanvasDocument) => CanvasDocument
  ): void;
  runServerCommand<Result extends { canvas: CanvasView }>(
    command: (revision: string) => Promise<Result>,
    merge: (current: CanvasDocument, result: Result) => CanvasDocument
  ): Promise<Result | undefined>;
  runWithSavedRevision<Result>(
    command: (revision: string) => Promise<Result>
  ): Promise<Result | undefined>;
  copyLocalToNewProject(): Promise<void>;
}

export async function copyLocalCanvasToNewProject({
  client,
  projectName,
  local,
  navigate
}: {
  client: Pick<ApiClient, "createProject" | "getCanvas" | "saveCanvas">;
  projectName: string;
  local: CanvasDocument;
  navigate?: ((path: string) => void) | undefined;
}): Promise<void> {
  const project = await client.createProject({ name: `${projectName} 副本` });
  const created = await client.getCanvas(project.id);
  await client.saveCanvas(project.id, { revision: created.revision, document: local });
  navigate?.(`/projects/${project.id}`);
}

export async function recoverStoredCanvasDraft({
  ownerId,
  projectId,
  drafts,
  controller
}: {
  ownerId: string;
  projectId: string;
  drafts: CanvasDraftStore;
  controller: Pick<
    CanvasSessionController,
    "getMutationVersion" | "recoverDraft"
  >;
}): Promise<CanvasDraftListing> {
  const expectedMutationVersion = controller.getMutationVersion();
  const listing = await drafts.listWithDiagnostics(ownerId, projectId);
  if (listing.invalidCount === 0 && listing.drafts.length === 1) await controller.recoverDraft(listing.drafts[0]!, expectedMutationVersion);
  return listing;
}

export function canvasDraftListingError(invalidCount:number):string|undefined {
  return invalidCount > 0 ? `${invalidCount} ${canvasCopy.recoveryPartialReadFailed}` : undefined;
}

export function useCanvasSession(options: UseCanvasSessionOptions): CanvasSession {
  const client = options.client ?? apiClient;
  const draftsRef = useRef(options.drafts ?? createCanvasDraftStore());
  const controllerRef = useRef<CanvasSessionController | undefined>(undefined);
  const [document, setDocument] = useState(options.initialCanvas.document);
  const [recoveryState, setRecoveryState] = useState<CanvasRecoveryState>({ durable: true, localError: false });
  const [recoveryDrafts, setRecoveryDrafts] = useState<CanvasDraft[]>([]);
  const [recoveryError, setRecoveryError] = useState<string>();
  useBlocker(() => !confirmCanvasNavigation(
    controllerRef.current?.getRecoveryState() ?? { durable: true, localError: false },
    window.confirm.bind(window)
  ));
  const [saveState, setSaveState] = useState<SaveState>({
    status: "saved",
    revision: options.initialCanvas.revision
  });

  useEffect(() => {
    const controller = createCanvasSessionController({
      ownerId: options.ownerId,
      projectId: options.projectId,
      initialRevision: options.initialCanvas.revision,
      initialDocument: options.initialCanvas.document,
      saveCanvas: client.saveCanvas.bind(client),
      drafts: draftsRef.current,
      onDocument: setDocument,
      onRecoveryState: setRecoveryState,
      onState: setSaveState
    });
    controllerRef.current = controller;
    setDocument(options.initialCanvas.document);
    setSaveState({ status: "saved", revision: options.initialCanvas.revision });
    setRecoveryState(controller.getRecoveryState());
    setRecoveryDrafts([]);
    setRecoveryError(undefined);
    const beforeUnload = (event: BeforeUnloadEvent) => protectCanvasUnload(event, controller.getRecoveryState());
    window.addEventListener("beforeunload", beforeUnload);
    void recoverStoredCanvasDraft({
      ownerId: options.ownerId,
      projectId: options.projectId,
      drafts: draftsRef.current,
      controller
    }).then((listing) => {
      if (controllerRef.current === controller) {
        setRecoveryDrafts(listing.drafts);
        setRecoveryError(canvasDraftListingError(listing.invalidCount));
      }
    }).catch(() => {
      if (controllerRef.current === controller) setRecoveryError(canvasCopy.recoveryReadFailed);
    });
    return () => {
      controller.dispose();
      window.removeEventListener("beforeunload", beforeUnload);
      if (controllerRef.current === controller) controllerRef.current = undefined;
    };
  }, [client, options.initialCanvas, options.ownerId, options.projectId]);

  const change = useCallback((nextDocument: CanvasDocument) => {
    controllerRef.current?.change(nextDocument);
  }, []);
  const retry = useCallback(() => controllerRef.current?.retry(), []);
  const getMutationVersion = useCallback(
    () => controllerRef.current?.getMutationVersion() ?? 0,
    []
  );
  const refreshRecoveryDrafts = useCallback(async () => {
    const controller = controllerRef.current;
    try {
      const listing = await draftsRef.current.listWithDiagnostics(options.ownerId, options.projectId);
      if (controllerRef.current === controller) {
        setRecoveryDrafts(listing.drafts);
        setRecoveryError(canvasDraftListingError(listing.invalidCount));
      }
    } catch {
      if (controllerRef.current === controller) setRecoveryError(canvasCopy.recoveryReadFailed);
    }
  }, [options.ownerId, options.projectId]);
  const restoreDraft = useCallback((draft: CanvasDraft) => {
    const controller = controllerRef.current;
    if (!controller || !confirmCanvasNavigation(controller.getRecoveryState(), window.confirm.bind(window))) return;
    void controller.recoverDraft(draft, controller.getMutationVersion()).catch(() => {
      if (controllerRef.current === controller) setRecoveryError(canvasCopy.draftConversionFailed);
    });
  }, []);
  const exportLocal = useCallback(() => exportLatestCanvas(() => controllerRef.current?.getDocument()), []);
  const loadServer = useCallback(async () => {
    const controller = controllerRef.current;
    if (!controller) return;
    const version = controller.getMutationVersion();
    try {
      const view = await client.getCanvas(options.projectId);
      if (controllerRef.current !== controller) return;
      if (controller.getMutationVersion() !== version) throw new Error("Local edit during reload");
      if (!confirmCanvasNavigation(controller.getRecoveryState(), window.confirm.bind(window))) return;
      await controller.replaceWithServer(view);
      await refreshRecoveryDrafts();
    } catch {
      if (controllerRef.current === controller) setRecoveryError(canvasCopy.recoveryActionFailed);
    }
  }, [client, options.projectId, refreshRecoveryDrafts]);
  const copyLocalToNewProject = useCallback(async () => {
    const controller = controllerRef.current;
    const local = controller?.getDocument();
    if (!local) return;
    try {
      await copyLocalCanvasToNewProject({
        client,
        projectName: options.projectName,
        local,
        navigate: (path) => {
          if (controllerRef.current === controller) options.navigate?.(path);
        }
      });
    } catch {
      if (controllerRef.current === controller) setRecoveryError(canvasCopy.recoveryActionFailed);
    }
  }, [client, options.navigate, options.projectName]);
  const applyServerCommand = useCallback(
    (
      view: CanvasView,
      merge: (current: CanvasDocument, server: CanvasDocument) => CanvasDocument
    ) => controllerRef.current?.applyServerCommand(view, merge),
    []
  );
  const runServerCommand = useCallback(
    <Result extends { canvas: CanvasView }>(
      command: (revision: string) => Promise<Result>,
      merge: (current: CanvasDocument, result: Result) => CanvasDocument
    ) => controllerRef.current?.runServerCommand(command, merge) ?? Promise.resolve(undefined),
    []
  );
  const runWithSavedRevision = useCallback(
    <Result>(command: (revision: string) => Promise<Result>) =>
      controllerRef.current?.runWithSavedRevision(command) ?? Promise.resolve(undefined),
    []
  );

  return {
    document,
    saveState,
    recoveryState,
    recoveryDrafts,
    recoveryError,
    refreshRecoveryDrafts,
    restoreDraft,
    exportLocal,
    change,
    retry,
    getMutationVersion,
    loadServer,
    applyServerCommand,
    runServerCommand,
    runWithSavedRevision,
    copyLocalToNewProject
  };
}
